import React from 'react';
import { DashboardLayout } from '../../client/components/Sidebar';
import DefaultAdminLayout from '../../admin/layout/DefaultLayout';
import { type AuthUser } from 'wasp/auth';
import { BreadcrumbNav, BreadcrumbItem } from '../../shared/components/BreadcrumbNav';

export interface IndividualViewPageLayoutProps {
  children: React.ReactNode;
  sidebarContent?: React.ReactNode;
  breadcrumbItems?: BreadcrumbItem[];
  currentPageUrl?: string;
  customTopBarContent?: React.ReactNode;
  layoutVariant?: 'dashboard' | 'admin' | 'taxonomy';
  authUser?: AuthUser;
}

/**
 * Reusable layout component for individual item view pages.
 * Provides consistent structure with main content area and right sidebar.
 * 
 * @example
 * ```tsx
 * <IndividualViewPageLayout
 *   breadcrumbItems={breadcrumbItems}
 *   currentPageUrl={window.location.href}
 *   sidebarContent={
 *     <>
 *       <ActionButtons />
 *       <DetailsSection />
 *     </>
 *   }
 * >
 *   <MainContent />
 * </IndividualViewPageLayout>
 * ```
 */
export default function IndividualViewPageLayout({
  children,
  sidebarContent,
  breadcrumbItems,
  currentPageUrl,
  customTopBarContent,
  layoutVariant = 'dashboard',
  authUser
}: IndividualViewPageLayoutProps) {
  // Generate breadcrumb content if items are provided
  const breadcrumbContent = breadcrumbItems && currentPageUrl ? (
    <BreadcrumbNav
      items={breadcrumbItems}
      currentPageUrl={currentPageUrl}
    />
  ) : customTopBarContent;

  const pageContent = (
    <div className="min-h-screen bg-background">
      <div className="w-full">
        <div className="w-full">
          <div className="flex gap-6 items-start">
            {/* Main Content */}
            <div className={`flex-1 min-w-0 ${layoutVariant === 'admin' || layoutVariant === 'taxonomy' ? 'pl-6 pt-4' : 'px-6 py-4 md:px-8 md:py-6 2xl:px-10 2xl:py-8'}`}>
              {children}
            </div>

            {/* Right Sidebar */}
            {sidebarContent && (
              <div className="w-80 hidden lg:flex border-l border-border flex-col sticky top-0 self-start ml-auto bg-card">
                {sidebarContent}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (layoutVariant === 'admin' && authUser) {
    return <DefaultAdminLayout user={authUser}>{pageContent}</DefaultAdminLayout>;
  }

  // 'taxonomy' — App.tsx already provides TaxonomyLayout, just render content
  if (layoutVariant === 'taxonomy') {
    return pageContent;
  }

  return <DashboardLayout customTopBarContent={breadcrumbContent}>{pageContent}</DashboardLayout>;
}




